import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { defaultTableLayout, type TableLayout } from '../layout'
import { FilterMatch, type TableQuery } from '../schemas'
import { tableLayoutRequest } from '../fetch/table-layout'
import { ApiError } from '@/util/errors/api-error'

type LayoutSession = {
  tableId: string
  layout: TableLayout
  loaded: boolean
  revision: number
  active: boolean
  pending: Partial<TableLayout>
  timer?: ReturnType<typeof setTimeout>
  queue: Promise<void>
}

function viewKey(layout: TableLayout) {
  return JSON.stringify([layout.filters, layout.sorts, layout.filter_match, layout.search])
}

/** Personal view changes immediately; coalesce and serialize only the settings that changed. */
export function useTableLayout(tableId: string, refreshKey = '') {
  const [state, setState] = useState({ tableId, layout: defaultTableLayout, loaded: false, saving: false, error: '', loadErrorStatus: null as number | null })
  const [attempt, setAttempt] = useState(0)
  const [pagination, setPagination] = useState({ offset: 0, limit: 100 })
  const session = useRef<LayoutSession>({
    tableId,
    layout: defaultTableLayout,
    loaded: false,
    revision: 0,
    active: false,
    pending: {},
    queue: Promise.resolve(),
  })
  const flush = useCallback((current: LayoutSession) => {
    clearTimeout(current.timer)
    if (!Object.keys(current.pending).length) return
    current.queue = current.queue.then(async () => {
      if (!Object.keys(current.pending).length) return
      const patch = current.pending
      current.pending = {}
      try {
        const response = await tableLayoutRequest(current.tableId, patch)
        if (current.active) {
          const merged = { ...defaultTableLayout, ...response, ...current.pending }
          const viewChanged = viewKey(merged) !== viewKey(current.layout)
          const layout = viewChanged ? merged : { ...merged, filters: current.layout.filters, sorts: current.layout.sorts }
          if (viewChanged) setPagination((value) => ({ ...value, offset: 0 }))
          current.layout = layout
          setState((s) => ({ ...s, layout: current.layout, saving: Object.keys(current.pending).length > 0, error: '' }))
        }
      } catch {
        current.pending = { ...patch, ...current.pending }
        if (current.active) setState((s) => ({ ...s, saving: false, error: 'Your table view could not be saved. Retry to remember these changes.' }))
      }
    })
  }, [])
  useEffect(() => {
    const current: LayoutSession = { tableId, layout: defaultTableLayout, loaded: false, revision: 0, active: true, pending: {}, queue: Promise.resolve() }
    session.current = current
    const controller = new AbortController()
    setPagination({ offset: 0, limit: 100 })
    setState({ tableId, layout: defaultTableLayout, loaded: false, saving: false, error: '', loadErrorStatus: null })
    current.queue = tableLayoutRequest(tableId, undefined, controller.signal)
      .then((response) => {
        if (!current.active) return
        current.layout = { ...defaultTableLayout, ...response }
        current.loaded = true
        setState({ tableId, layout: current.layout, loaded: true, saving: false, error: '', loadErrorStatus: null })
      })
      .catch((error: unknown) => {
        if (current.active)
          setState((s) => ({
            ...s,
            error: error instanceof Error ? error.message : 'Unable to load your table view.',
            loadErrorStatus: error instanceof ApiError ? error.status : null,
          }))
      })
    return () => {
      current.active = false
      controller.abort()
      flush(current)
    }
  }, [tableId, attempt, flush])

  const previousRefresh = useRef(refreshKey)
  useEffect(() => {
    if (previousRefresh.current === refreshKey) return
    previousRefresh.current = refreshKey
    const current = session.current
    flush(current)
    current.queue = current.queue.then(async () => {
      if (!current.active || !current.loaded || Object.keys(current.pending).length) return
      const revision = current.revision
      try {
        const response = await tableLayoutRequest(current.tableId)
        if (!current.active || current.revision !== revision) return
        const merged = { ...defaultTableLayout, ...response }
        const viewChanged = viewKey(merged) !== viewKey(current.layout)
        const layout = viewChanged ? merged : { ...merged, filters: current.layout.filters, sorts: current.layout.sorts }
        if (viewChanged) {
          setPagination((value) => ({ ...value, offset: 0 }))
        }
        current.layout = layout
        setState((s) => ({ ...s, layout, error: '' }))
      } catch {
        if (current.active) setState((s) => ({ ...s, error: 'Unable to refresh your table view. Retry to load the saved changes.' }))
      }
    })
  }, [refreshKey, flush])

  const change = useCallback(
    (update: (layout: TableLayout) => TableLayout) => {
      const current = session.current
      if (!current.active || !current.loaded) return
      const layout = update(current.layout)
      const patch = Object.fromEntries(
        Object.entries(layout).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(current.layout[key as keyof TableLayout]))
      ) as Partial<TableLayout>
      if (!Object.keys(patch).length) return
      current.layout = layout
      current.pending = { ...current.pending, ...patch }
      current.revision += 1
      setState((s) => ({ ...s, layout, saving: true, error: '' }))
      clearTimeout(current.timer)
      // Batch a short sequence of filter, sort, or resize interactions into one preference save.
      current.timer = setTimeout(() => flush(current), 400)
    },
    [flush]
  )
  const loaded = state.tableId === tableId && state.loaded
  const layout = loaded ? state.layout : defaultTableLayout
  const query = useMemo<TableQuery>(
    () => ({
      filters: layout.filters,
      sorts: layout.sorts,
      filter_match: layout.filter_match,
      search: layout.search,
      ...pagination,
    }),
    [layout.filters, layout.sorts, layout.filter_match, layout.search, pagination]
  )
  const queryRef = useRef(query)
  queryRef.current = query
  const setQuery = useCallback(
    (update: SetStateAction<TableQuery>) => {
      const next = typeof update === 'function' ? update(queryRef.current) : update
      queryRef.current = next
      setPagination({ offset: next.offset, limit: next.limit })
      change((current) => ({
        ...current,
        filters: next.filters,
        sorts: next.sorts,
        filter_match: next.filter_match ?? FilterMatch.All,
        search: next.search ?? '',
      }))
    },
    [change]
  )
  return {
    layout,
    loaded,
    saving: state.saving,
    error: state.error,
    loadErrorStatus: state.tableId === tableId ? state.loadErrorStatus : null,
    change,
    query,
    setQuery,
    retry: () => {
      if (Object.keys(session.current.pending).length) {
        setState((s) => ({ ...s, saving: true, error: '' }))
        flush(session.current)
      } else setAttempt((value) => value + 1)
    },
  }
}
